import type { Probe, ProbeResult } from '../engine/types';
import { ok, na } from '../engine/runner';
import { registerProbe } from '../engine/registry';

const MATH_SOURCE = 'https://tc39.es/ecma262/#sec-math-objects';

/**
 * Math-function precision fingerprint.
 *
 * Transcendental functions (Math.tan, Math.sin, Math.exp, Math.log1p, …) are
 * not required to be bit-exact, and V8 / SpiderMonkey / JSC / QuickJS all
 * round differently. Feeding fixed inputs and hashing the exact float64
 * bit patterns yields a cheap, highly engine-specific value that requires no
 * permissions and no heavy GPU work — a classic low-cost entropy source.
 */
export const mathProbe: Probe = {
  id: 'mathFingerprint',
  label: 'Math precision',
  layer: 'engine',
  tier: 'client',
  category: 'browser',
  description: 'Transcendental function bit-exactness fingerprint',
  sources: [MATH_SOURCE],
  budgetMs: 500,

  async run(): Promise<ProbeResult> {
    if (typeof Math === 'undefined') {
      return { signals: { available: na<boolean>('unsupported', 'client', 'no Math') } };
    }

    const samples: Record<string, number> = {
      acos: Math.acos(0.12312423423423424),
      acosh: Math.acosh(1e300),
      asin: Math.asin(0.12312423423423424),
      asinh: Math.asinh(1),
      atanh: Math.atanh(0.5),
      atan: Math.atan(0.5),
      sin: Math.sin(-1e300),
      sinh: Math.sinh(1),
      cos: Math.cos(10.000000000123),
      cosh: Math.cosh(1),
      tan: Math.tan(-1e300),
      tanh: Math.tanh(1),
      exp: Math.exp(1),
      expm1: Math.expm1(1),
      log1p: Math.log1p(10),
      powPI: Math.pow(Math.PI, -100),
    };

    // Exact float64 bit patterns — the part that actually differs per engine.
    const bits: Record<string, string> = {};
    const buf = new ArrayBuffer(8);
    const f64 = new Float64Array(buf);
    const u8 = new Uint8Array(buf);
    for (const [k, v] of Object.entries(samples)) {
      f64[0] = v;
      bits[k] = Array.from(u8)
        .map((b) => b.toString(16).padStart(2, '0'))
        .join('');
    }

    const hash = await fnv1aHex(JSON.stringify(bits));
    // tanh(0.8) is a common additional probe; it is stable so we report it
    // separately rather than in the hashed set.
    const tanh08 = Math.tanh(0.8);

    return {
      signals: {
        available: ok(true, 'client'),
        samples: ok(samples, 'client'),
        // Bit patterns are what we hash; the floats are for human inspection.
        sampleBits: ok(bits, 'client'),
        hash: ok(hash, 'client'),
        tanh08: ok(tanh08, 'client'),
      },
      parts: {
        browserFeatures: {
          // Engine precision is a stable engine property, so it belongs in the
          // fingerprint (unlike timing values elsewhere).
          mathHash: hash,
        },
      },
    };
  },
};

async function fnv1aHex(input: string): Promise<string> {
  let h1 = 0x811c9dc5;
  let h2 = 0x01000193;
  for (let i = 0; i < input.length; i++) {
    const c = input.charCodeAt(i);
    h1 ^= c;
    h1 = Math.imul(h1, 0x01000193) >>> 0;
    h2 = (Math.imul(h2 ^ c, 0x85ebca6b) >>> 0) ^ (h2 >>> 13);
  }
  return (h1.toString(16).padStart(8, '0') + (h2 >>> 0).toString(16).padStart(8, '0')).slice(0, 16);
}

registerProbe(mathProbe);
